import { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect } from "react";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import { VendorPOService } from "@/services/vendor.service";
import { FieldMaterialFeedbackService } from "@/services/field-material-feedback.service";
import { SubstitutionRequestService } from "@/services/substitution-request.service";

/* ═══════════════════════════════════════════════════
   Vendor Management & Purchase Order System
   ═══════════════════════════════════════════════════ */

export type OrderingMethod = "Email" | "Portal" | "Manual";
export type POStatus = "Draft" | "Submitted" | "Confirmed" | "In Transit" | "Delivered" | "Received";

/* ── Procurement audit trail ── */
export interface ProcurementAuditEvent {
  id: string;
  subjectType: "po" | "receiving" | "allocation" | "feedback" | "substitution";
  subjectId: string;
  action: string;
  actor: string;
  reason?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

/* ── Field Material Feedback (Phase 4) ── */
export type FieldMaterialSeverity = "blocker" | "warn" | "info";
export type FieldMaterialIssueType =
  | "wrong_material"
  | "damaged"
  | "missing"
  | "short_shipped"
  | "quality_defect"
  | "measurement_error"
  | "spec_mismatch"
  | "other";

export interface FieldMaterialFeedbackEvent {
  id: string;
  jobId: string;
  createdAt: string;
  actor: string;
  issueType: FieldMaterialIssueType;
  severity: FieldMaterialSeverity;
  description: string;
  materialReference?: string;
  phase?: string;
  photoEvidence?: string[];
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
  canonicalPoId?: string;
  lineageAmbiguous?: boolean;
  lineageAmbiguousReason?: string;
}

/* ── Substitution Requests (Phase 5) ── */
export type SubstitutionReason =
  | "vendor_unavailable"
  | "vendor_delay"
  | "cost_variance"
  | "spec_change"
  | "damage_replacement"
  | "field_issue"
  | "other";
export type SubstitutionCompatibility = "unknown" | "compatible" | "incompatible" | "conditional";
export type SubstitutionCustomerImpact = "unknown" | "none" | "minor" | "significant" | "requires_approval";
export type SubstitutionStatus = "pending_review" | "approved" | "rejected" | "superseded";

export interface SubstitutionRequest {
  id: string;
  jobId: string;
  createdAt: string;
  createdBy: string;
  originalDescription: string;
  proposedDescription: string;
  reason: SubstitutionReason;
  originalMaterialReference?: string;
  proposedMaterialReference?: string;
  compatibilityAssessment: SubstitutionCompatibility;
  compatibilityNotes?: string;
  customerImpact: SubstitutionCustomerImpact;
  customerApprovalRequired: boolean;
  status: SubstitutionStatus;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
  canonicalPoId?: string;
  lineageAmbiguous?: boolean;
  lineageAmbiguousReason?: string;
}

export interface Vendor {
  vendorId: string;
  vendorName: string;
  contactName: string;
  phone: string;
  email: string;
  orderingMethod: OrderingMethod;
  notes: string;
  active: boolean;
}

export interface POLineItem {
  sku: string;
  productName: string;
  quantity: number;
  unitCost: number;
  lineTotal: number;
}

export type POTrackingPurpose = "inbound" | "outbound" | "return" | "reship" | "replacement" | "other";

export type TrackingStatusTag =
  | "Pending"
  | "InfoReceived"
  | "InTransit"
  | "OutForDelivery"
  | "Delivered"
  | "Exception"
  | "Expired";

/** @deprecated use TrackingStatusTag */
export type AfterShipTag = TrackingStatusTag;

export interface POTrackingEntry {
  id: string;
  trackingNumber: string;
  carrier?: string;
  purpose?: POTrackingPurpose;
  notes?: string;
  createdAt: string;
  trackingStatus?: TrackingStatusTag;
  currentLocation?: string;
  estimatedDelivery?: string;
  lastEventAt?: string;
  lastEventDescription?: string;
}

export interface PurchaseOrder {
  poId: string;
  poNumber: string;
  externalPoNumber?: string;
  jobId: string;
  jobLabel: string;
  vendorId: string;
  vendorName: string;
  items: POLineItem[];
  status: POStatus;
  totalCost: number;
  createdAt: string;
  submittedAt?: string;
  confirmedAt?: string;
  deliveredAt?: string;
  /**
   * Vendor-side operational state (Phase 2 canonical writers).
   * - `etaConfirmed`   – vendor has actively acknowledged / confirmed the ETA.
   * - `vendorAckAt`    – ISO timestamp when the ack was recorded.
   * - `estimatedEta`   – current best-guess delivery date (revisable without flipping `etaConfirmed`).
   * - `confirmedEta`   – the ETA value that was confirmed by the vendor.
   * - `ackReference`   – free-text reference number from the vendor's acknowledgement.
   */
  etaConfirmed: boolean;
  vendorAckAt?: string;
  estimatedEta?: string;
  confirmedEta?: string;
  ackReference?: string;
  trackingNumbers?: POTrackingEntry[];
  notes: string;
  flagged: boolean;
  flagReason?: string;
  auditEvents?: ProcurementAuditEvent[];
  warehouseId?: string | null;
  warehouseName?: string | null;
}

export interface VendorPOContextValue {
  vendors: Vendor[];
  setVendors: React.Dispatch<React.SetStateAction<Vendor[]>>;
  purchaseOrders: PurchaseOrder[];
  setPurchaseOrders: React.Dispatch<React.SetStateAction<PurchaseOrder[]>>;
  loading: boolean;
  getVendorById: (id: string) => Vendor | undefined;
  getVendorByName: (name: string) => Vendor | undefined;
  getPOsByJob: (jobId: string) => PurchaseOrder[];
  getPOsByVendor: (vendorId: string) => PurchaseOrder[];
  getPOsByStatus: (status: POStatus) => PurchaseOrder[];
  generatePONumber: () => string;
  refreshData: () => Promise<void>;
  fieldMaterialFeedback: FieldMaterialFeedbackEvent[];
  setFieldMaterialFeedback: React.Dispatch<React.SetStateAction<FieldMaterialFeedbackEvent[]>>;
  substitutionRequests: SubstitutionRequest[];
  setSubstitutionRequests: React.Dispatch<React.SetStateAction<SubstitutionRequest[]>>;
  getFieldFeedbackByJob: (jobId: string) => FieldMaterialFeedbackEvent[];
  getSubstitutionRequestsByJob: (jobId: string) => SubstitutionRequest[];
}

const SEED_VENDORS: Vendor[] = [
  { vendorId: "v-001", vendorName: "Sentrel", contactName: "Mike Reynolds", phone: "(813) 555-0101", email: "orders@sentrel.com", orderingMethod: "Email", notes: "Primary wall panel supplier", active: true },
  { vendorId: "v-002", vendorName: "Cosentino", contactName: "Laura Chen", phone: "(813) 555-0202", email: "orders@cosentino.com", orderingMethod: "Portal", notes: "Dekton surfaces — use dealer portal", active: true },
  { vendorId: "v-003", vendorName: "Daltile", contactName: "James Park", phone: "(813) 555-0303", email: "commercial@daltile.com", orderingMethod: "Email", notes: "Tile supplier", active: true },
  { vendorId: "v-004", vendorName: "Coastal Shower Doors", contactName: "Sarah Wells", phone: "(813) 555-0404", email: "orders@coastalshowerdoors.com", orderingMethod: "Email", notes: "Glass enclosures and doors", active: true },
  { vendorId: "v-005", vendorName: "Delta", contactName: "Tom Bradley", phone: "(813) 555-0505", email: "pro@deltafaucet.com", orderingMethod: "Portal", notes: "Fixtures and faucets", active: true },
  { vendorId: "v-006", vendorName: "Moen", contactName: "Rachel Gomez", phone: "(813) 555-0606", email: "commercial@moen.com", orderingMethod: "Email", notes: "Grab bars and accessories", active: true },
  { vendorId: "v-007", vendorName: "Better Bath", contactName: "Kevin Hart", phone: "(813) 555-0707", email: "orders@betterbath.com", orderingMethod: "Manual", notes: "Seats and add-on accessories", active: true },
  { vendorId: "v-008", vendorName: "Schluter", contactName: "Anna Müller", phone: "(813) 555-0808", email: "orders@schluter.com", orderingMethod: "Email", notes: "Waterproofing and drainage", active: true },
  { vendorId: "v-009", vendorName: "Ella's Bubbles", contactName: "David Kim", phone: "(813) 555-0909", email: "orders@ellasbubbles.com", orderingMethod: "Portal", notes: "Walk-in tub systems", active: true },
];

// const SEED_POS: PurchaseOrder[] = [
//   {
//     poId: "po-001", poNumber: "PO-1001", jobId: "JOB-001", jobLabel: "Johnson – Dekton Shower",
//     vendorId: "v-002", vendorName: "Cosentino",
//     items: [
//       { sku: "WALL-DEK-STD", productName: "Dekton Wall Panel – Standard", quantity: 1, unitCost: 980, lineTotal: 980 },
//       { sku: "BASE-DEK-CUST", productName: "Dekton Custom Base", quantity: 1, unitCost: 650, lineTotal: 650 },
//     ],
//     status: "Confirmed", totalCost: 1630, createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
//     submittedAt: new Date(Date.now() - 4 * 86400000).toISOString(),
//     confirmedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
//     notes: "", flagged: false,
//   },
//   {
//     poId: "po-002", poNumber: "PO-1002", jobId: "JOB-001", jobLabel: "Johnson – Dekton Shower",
//     vendorId: "v-004", vendorName: "Coastal Shower Doors",
//     items: [
//       { sku: "GLS-CLR-STD", productName: "Clear Glass Panel – Standard", quantity: 1, unitCost: 380, lineTotal: 380 },
//     ],
//     status: "Submitted", totalCost: 380, createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
//     submittedAt: new Date(Date.now() - 4 * 86400000).toISOString(),
//     notes: "", flagged: false,
//   },
// ];

let poCounter = 1003;

const VendorPOContext = createContext<VendorPOContextValue | null>(null);

export function VendorPOProvider({ children }: { children: ReactNode }) {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [fieldMaterialFeedback, setFieldMaterialFeedback] = useState<FieldMaterialFeedbackEvent[]>([]);
  const [substitutionRequests, setSubstitutionRequests] = useState<SubstitutionRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

   const refreshData = useCallback(async () => {
     if (deferHeavyProvider) {
       setLoading(false);
       return;
     }

     setLoading(true);
     try {
       const [dbVendors, dbOrders, dbFeedback, dbSubstitutions] = await Promise.all([
         VendorPOService.fetchVendors(),
         VendorPOService.fetchPurchaseOrders(),
         FieldMaterialFeedbackService.fetchAll(),
         SubstitutionRequestService.fetchAll(),
       ]);
       setVendors(dbVendors);
       setPurchaseOrders(dbOrders);
       setFieldMaterialFeedback(dbFeedback);
       setSubstitutionRequests(dbSubstitutions);
     } finally {
       setLoading(false);
     }
   }, [deferHeavyProvider]);

   useEffect(() => {
     if (deferHeavyProvider) {
       setLoading(false);
       return;
     }

     let cancelled = false;
     const hydrate = async () => {
       setLoading(true);
       try {
         const [dbVendors, dbOrders, dbFeedback, dbSubstitutions] = await Promise.all([
           VendorPOService.fetchVendors(),
           VendorPOService.fetchPurchaseOrders(),
           FieldMaterialFeedbackService.fetchAll(),
           SubstitutionRequestService.fetchAll(),
         ]);
         if (cancelled) return;
         setVendors(dbVendors);
         setPurchaseOrders(dbOrders);
         setFieldMaterialFeedback(dbFeedback);
         setSubstitutionRequests(dbSubstitutions);
       } finally {
         if (!cancelled) setLoading(false);
       }
     };
     void hydrate();
     return () => {
       cancelled = true;
     };
   }, [deferHeavyProvider]);

  const getVendorById = useCallback((id: string) => vendors.find((v) => v.vendorId === id), [vendors]);
  const getVendorByName = useCallback((name: string) => vendors.find((v) => v.vendorName === name), [vendors]);
  const getPOsByJob = useCallback((jobId: string) => purchaseOrders.filter((po) => po.jobId === jobId), [purchaseOrders]);
  const getPOsByVendor = useCallback((vendorId: string) => purchaseOrders.filter((po) => po.vendorId === vendorId), [purchaseOrders]);
  const getPOsByStatus = useCallback((status: POStatus) => purchaseOrders.filter((po) => po.status === status), [purchaseOrders]);
  const generatePONumber = useCallback(() => `PO-${poCounter++}`, []);
  const getFieldFeedbackByJob = useCallback(
    (jobId: string) => fieldMaterialFeedback.filter((e) => e.jobId === jobId),
    [fieldMaterialFeedback],
  );
  const getSubstitutionRequestsByJob = useCallback(
    (jobId: string) => substitutionRequests.filter((r) => r.jobId === jobId),
    [substitutionRequests],
  );

  return (
    <VendorPOContext.Provider value={{
      vendors, setVendors, purchaseOrders, setPurchaseOrders,
      loading,
      getVendorById, getVendorByName, getPOsByJob, getPOsByVendor, getPOsByStatus, generatePONumber,
      refreshData,
      fieldMaterialFeedback, setFieldMaterialFeedback,
      substitutionRequests, setSubstitutionRequests,
      getFieldFeedbackByJob, getSubstitutionRequestsByJob,
    }}>
      {children}
    </VendorPOContext.Provider>
  );
}

export function useVendorPO() {
  const ctx = useContext(VendorPOContext);
  if (!ctx) throw new Error("useVendorPO must be used within VendorPOProvider");
  return ctx;
}
